import { useLazyQuery, useQuery } from "@apollo/client/react"; import React, { useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { RenderInputByTypeMobile } from '../../../../components/formGroupGenerator/RenderInputByTypeMobile';
import { getStringQueryCombosRI } from '../../../../data/graphQL/queries/ResumenIngreso/getAllCombosRI';
import { adaptCombosRIArray } from '../../../../adapters/resumenIngreso/combosRIAdapter';

import { CALCULAR_SCHOFIELD } from '../../../../data/graphQL/queries/ResumenIngreso/calcularSchofield';
import { obtenerDataImagen } from '../../../../actions/ImagenAction';

export const StepProgramaAP = ({
  setDataSubmissionCallback,
  esAdulto,
  updateFormData,
  paciente,
  fullFormDataRI
}) => {

  const [datosProgramaAP, setDatosProgramaAP] = useState(fullFormDataRI?.programaAP1 ?? {});
  const [combosRI, setCombosRI] = useState();

  const inputCombos = inputsGroups.flatMap((inputgroup) => {
    return inputgroup.inputs.filter((input) => !!input.comboName);
  });

  const { data: combosData } = useQuery(getStringQueryCombosRI(inputCombos));

  useEffect(() => {
    if (!combosData) return;

    setCombosRI(adaptCombosRIArray(combosData));
  }, [combosData]);

  const [getCalculoSchofield] = useLazyQuery(CALCULAR_SCHOFIELD, {
    skip: esAdulto
  });

  const onDeleteImage = (inputName) => handleChange(inputName, null);

  const parsedInputsGroup = useMemo(() => {
    const filteredGroups = inputsGroups.filter((group) => !group[esAdulto ? 'esPed' : 'esAdulto']);
    return filteredGroups.map((group) => ({
      ...group,
      inputs: group.inputs.map((input) => {
        switch (input.name) {          
          case "calorias":
          case "hidratosCarbono":
          case "proteinas":
          case "lipidos":
            const { min, max } = esAdulto ? input.limiteAdulto : input.limitePediatrico;
            input.min = min;
            input.max = max;
            break;          
          default:
            break;
        }

        if (!!input.comboName && !!combosRI) {
          input.options = combosRI[input.name];
        }
        if (input.name !== 'laboratorioAdjunto') return input;

        return {
          ...input,
          onDeleteImage: () => onDeleteImage(input.name)
        };

      })
    }));
  }, [esAdulto, combosRI]);

  useEffect(() => {
    const { edad, sexo } = paciente;
    const tallaEnMetros = fullFormDataRI?.datosGenerales3?.talla;
    const peso = fullFormDataRI?.datosGenerales3?.pesoActual;
    const existeSchofield = !!fullFormDataRI?.programaAP1?.formulaSchofieldPed;


    if (esAdulto || edad === null || edad === undefined || !sexo || !tallaEnMetros || !peso || existeSchofield) return;

    const getCalculos = async () => {
      const { data } = await getCalculoSchofield({
        variables: {
          tallaEnMetros,
          peso,
          edad,
          sexo
        }
      });

      setDatosProgramaAP((prevD) => ({
        ...prevD,
        formulaSchofieldPed: data.calcularSchofield
      }));
    };
    getCalculos();

  }, [
    paciente,
    fullFormDataRI?.datosGenerales3?.talla,
    fullFormDataRI?.datosGenerales3?.pesoActual,
    fullFormDataRI?.programaAP1?.formulaSchofieldPed,
    esAdulto]
  );

  useEffect(() => {
    setDatosProgramaAP((prevDatos) => {
      const { calorias = 0, proteinas = 0, lipidos = 0 } = prevDatos;
      const pesoActual = fullFormDataRI?.datosGenerales3?.pesoActual;
      return {
        ...prevDatos,
        caloriasTotales: calorias * pesoActual,
        proteinasTotales: proteinas * pesoActual,
        lipidosTotales: lipidos * pesoActual
      };
    });
  }, [
    datosProgramaAP.calorias,
    datosProgramaAP.proteinas,
    datosProgramaAP.lipidos,
    fullFormDataRI?.datosGenerales3?.pesoActual]
  );

  useEffect(() => {
    setDataSubmissionCallback(handleSubmit);
  }, [datosProgramaAP, setDataSubmissionCallback, parsedInputsGroup]);

  const handleSubmit = () => {
    updateFormData('programaAP1', datosProgramaAP, parsedInputsGroup.flatMap((x) => x.inputs));
  };

  const handleChange = (name, value) => {
    setDatosProgramaAP((prevDatos) => ({
      ...prevDatos,
      [name]: value
    }));
  };

  const handleChangeImage = (name, value) => {
    obtenerDataImagen(value).then((image) => {
      handleChange(name,
        {
          contentType: image?.tipo,
          archivo: image?.data,
          extension: image?.extension,
          titulo: image?.nombre
        }
      );
    });
  };

  return (
    <div>
      {parsedInputsGroup.map((group) =>
        <div className='step-form-section-container'>
          <label className={`step-form-section-title`}>
            {group.groupName}
          </label>
          <Grid contaier>
            {group.inputs.map((input) =>
              <RenderInputByTypeMobile
                key={`${group.groupName}-${input.name}`}
                grided
                handleChange={input.name === 'laboratorioAdjunto' ? handleChangeImage : handleChange}
                handleDelete={() => onDeleteImage(input.name)}
                input={input}
                value={datosProgramaAP[input.name]} />

            )}
          </Grid>
        </div>
      )}
    </div>);

};

const inputsGroups = [
  {
    groupName: 'Paciente Adulto',
    esAdulto: true,
    inputs: [
      {
        name: 'idComboRIValorKarnofsky',
        displayName: 'Karnofsky',
        comboName: 'Karnofsky',
        required: true,
        type: 'select'
      }]

  },
  {
    groupName: 'Requerimientos',
    inputs: [
      {
        name: 'calorias',
        displayName: 'Calorías /kg/d',
        required: true,
        type: 'float',
        limiteAdulto: {
          min: 1,
          max: 50
        },
        limitePediatrico: {
          min: 0,
          max: 105
        }
      },
      {
        name: 'hidratosCarbono',
        displayName: 'Hidratos de Carbono /kg/d',
        required: true,
        type: 'float',
        limiteAdulto: {
          min: 1,
          max: 7
        },
        limitePediatrico: {
          min: 0,
          max: 19
        }
      },
      {
        name: 'proteinas',
        displayName: 'Proteínas /kg/d',
        required: true,
        type: 'float',
        limiteAdulto: {
          min: 0,
          max: 2.5
        },
        limitePediatrico: {
          min: 0,
          max: 3.5
        }
      },
      {
        name: 'lipidos',
        displayName: 'Lípidos /kg/d',
        required: true,
        type: 'float',
        limiteAdulto: {
          min: 0,
          max: 2
        },
        limitePediatrico: {
          min: 0,
          max: 3.5
        }
      },
      {
        name: 'proteinasTotales',
        displayName: 'Proteínas totales /d',
        required: true,
        disabled: true,
        type: 'float'
      },
      {
        name: 'lipidosTotales',
        displayName: 'Lípidos totales /d',
        required: true,
        disabled: true,
        type: 'float'
      },
      {
        name: 'caloriasTotales',
        displayName: 'Calorías totales /d',
        required: true,
        disabled: true,
        type: 'float'
      }]

  },
  {
    groupName: 'Paciente Pediatrico',
    esPed: true,
    inputs: [
      {
        name: 'formulaSchofieldPed',
        displayName: 'GER Formula de Schofield',
        required: true,
        disabled: true,
        type: 'string'
      }]

  },
  {
    groupName: 'Último laboratorio',
    inputs: [
      {
        name: 'laboratorio',
        displayName: 'Laboratorio',
        required: true,
        type: 'long_string'
      },
      {
        name: 'laboratorioAdjunto',
        displayName: 'Subir Laboratorio',
        required: true,
        type: 'file',
        validExtensions: ['PDF', 'PNG', 'JPG', 'HEIC', 'JPEG', 'JFIF'],
        maxFileSize: 8
      }]

  }];